import { useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import { useReportLockedCustomer } from "../../../contexts/CustomerLockTracker";
import { CustomerLockBadge } from "../../dashboard/customers/CustomerLockBanner";
import CustomerDialog from "../../dashboard/customers/dialog";
import { CustomerSelector } from "../moduleSelectors/customerSelector";
import { PMEntityPicker } from "./PMEntityPicker";
import { InitialsAvatar, PICKER_PAGE_SIZE, joinParts, rowsFrom } from "./pickerParts";

// Tripletex imports private persons as "customer"; manual rows use "private".
const TYPE_FILTERS = {
  company: ["company", "customer_company_supplier"],
  private: ["private", "customer"],
};

export const customerToItem = (row) =>
  row?.id == null
    ? null
    : {
        id: row.id,
        primary: row.displayTitle ?? row.name ?? `#${row.id}`,
        secondary: joinParts(
          row.customer_number ? `#${row.customer_number}` : null,
          [row.address, row.postal, row.place].filter(Boolean).join(" ")
        ),
        leading: <InitialsAvatar text={row.displayTitle ?? row.name} />,
        tag: row.profile_locked ? <CustomerLockBadge customer={row} /> : null,
        object: row,
      };

export async function fetchCustomerOptions({ query, filter }) {
  const types = TYPE_FILTERS[filter];
  const response = await axios.get("/customers/", {
    params: {
      query: query || undefined,
      page: 1,
      per_page: PICKER_PAGE_SIZE,
      sortBy: "updated_at",
      sort: "desc",
      filters: types ? { 0: { name: "customer_type", value: types } } : undefined,
    },
  });
  return rowsFrom(response).map(customerToItem);
}

/**
 * Customer picker (new design). Emits the same events as the legacy
 * CustomerSelector: `onChange({ target: { name, value, data } })`, and with
 * `setFieldValue` also `<name>` and `<name>_locked`. Keeps customer-lock
 * tracking and offers "new customer" through the legacy create form.
 */
export function CustomerPicker({
  name = "customer_id",
  label,
  value,
  onChange,
  setFieldValue,
  disabled = false,
  error,
  required = false,
  allowCreate = true,
}) {
  const { t } = useTranslation();
  const trackerSourceId = useId();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const selectedId = value?.id ?? null;
  useReportLockedCustomer(trackerSourceId, value?.id != null ? value : null);

  const filters = useMemo(
    () => [
      { key: "all", label: t("pickers.customer.filterAll") },
      { key: "company", label: t("pickers.customer.filterCompany") },
      { key: "private", label: t("pickers.customer.filterPrivate") },
    ],
    [t]
  );

  const emit = useCallback(
    (row) => {
      const id = row?.id ?? null;
      if (setFieldValue) {
        setFieldValue(name, id);
        setFieldValue(`${name}_locked`, Boolean(row?.profile_locked));
      }
      onChange?.({ target: { name, value: id, data: row ?? null } });
    },
    [name, onChange, setFieldValue]
  );

  return (
    <>
      <PMEntityPicker
        label={label ?? t("Customer")}
        labelAction={selectedId != null ? { action: () => setDetailOpen(true) } : null}
        required={required}
        value={customerToItem(value)}
        placeholder={t("pickers.customer.placeholder")}
        searchPlaceholder={t("pickers.customer.search")}
        disabled={disabled}
        error={error}
        loadOptions={fetchCustomerOptions}
        filters={filters}
        defaultFilter="all"
        pageSize={PICKER_PAGE_SIZE}
        onSelect={(item) => emit(item?.object ?? null)}
        footerAction={allowCreate ? { label: t("pickers.customer.create"), onClick: () => setCreateOpen(true) } : undefined}
      >
        {value?.profile_locked && <p className="pm-field-error">{t("customer.lock.create_blocked")}</p>}
      </PMEntityPicker>
      {createOpen && (
        <CustomerSelector
          showField={false}
          open
          startInCreateView
          name={name}
          onClose={() => setCreateOpen(false)}
          onChange={(event) => {
            setCreateOpen(false);
            if (event?.target?.data?.id != null) emit(event.target.data);
          }}
        />
      )}
      {detailOpen && selectedId != null && (
        <CustomerDialog
          open
          onClose={() => setDetailOpen(false)}
          readOnly
          selected={{ id: selectedId }}
        />
      )}
    </>
  );
}
